import { dayjs } from '@utils/dateFormatter';
import { useRef, useCallback, useMemo, useReducer, useEffect, useState } from 'react';
import { useGetLatest, actions, makePropGetter, loopHooks, reduceHooks } from 'react-table';
import { create } from 'yup/lib/array';
import { makeDefaultPluginHooks } from './hooks/defaults';
import { makeRender } from './hooks/reduceHooks';
import { useIsToday } from './hooks/useIsToday';

const defaultEmptyRender = () => <>&nbsp;</>;
const defaultRender = ({ cell: { dayOfMonth }, ...rest }: any) => <div>{dayOfMonth}</div>;
const defaultCellProps = ({}) => {};
const defaultReducer = (state: any, _action: any, _prevState: any) => state;

function applyDefaults(prop: any = {}) {
    const {
        stateReducer = defaultReducer,
        initialState = {},
        renderCell = defaultRender,
        ...rest
    } = prop;

    return {
        ...rest,
        renderCell,
        stateReducer,
        initialState,
    };
}

const DaysOfWeekSundayEnd = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
// const DaysOfWeekSundayStart = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
/**
 * Took the Idea from React-Tables for performance considerations and things.
 * This turned into a bit hard to update when state is internally controlled versus externally.
 *
 * This could take some rework/return to the drawing board
 *
 * @param props
 *
 * @param plugins
 * @returns
 */
export function useCalendarHook(props: any = {}, ...plugins: any[]) {
    props = applyDefaults(props);

    // Going to add selecting days and etc as defaults?
    plugins = [useIsToday, ...plugins];

    // Reference to the object containing EVERYTHING
    let instanceRef = useRef<{ [key: string]: any }>({});
    // Reading this online.. Says helps avoid potential memory leaks?
    // I should read more
    const getInstance = useGetLatest(instanceRef.current);

    // Add some defaults for safe keeping
    Object.assign(getInstance(), {
        ...props,
        plugins,
        hooks: makeDefaultPluginHooks(),
    });

    //Apply those plugins
    plugins.filter(Boolean).forEach((plugin) => {
        plugin(getInstance().hooks);
    });

    // hmmm
    const getHooks = useGetLatest(getInstance().hooks);
    getInstance().getHooks = getHooks;
    delete getInstance().hooks;

    const { initialState, stateReducer } = getInstance();
    const getStateReducer = useGetLatest(stateReducer);

    // Combine all plugin reducers
    const reducer = useCallback(
        (state: any, action: any) => {
            return [
                ...getHooks().stateReducers,
                ...(Array.isArray(getStateReducer()) ? getStateReducer() : [getStateReducer()]),
            ].reduce((s, handler) => handler(s, action, state, getInstance()) || s, state);
        },
        [getHooks, getStateReducer, getInstance],
    );

    /**
     * For calendar this will never change
     * However in the near furture perhaps allowing start of week to change will be added
     */
    const headers = useMemo(() => {
        return DaysOfWeekSundayEnd;
    }, []);

    //TODO: Rename to state
    const [state, dispatch] = useReducer(reducer, undefined, () =>
        reducer(initialState, { type: actions.init }),
    );

    Object.assign(getInstance(), {
        state,
        dispatch,
    });

    const createRows = useCallback((dateUse: any) => {
        const startOfMonth = dayjs(dateUse).startOf('month');
        const endOfMonth = dayjs(dateUse).endOf('month');
        const dayOfWeekMonthStart = dayjs(startOfMonth).day();
        const dayOfWeekMonthEnd = dayjs(endOfMonth).day();
        const daysInMonth = dayjs(dateUse).daysInMonth();
        let i = 0;
        let rows = [];
        const dayEnd = dayOfWeekMonthStart;

        while (i < dayEnd) {
            rows.push({
                value: null,
                render: defaultEmptyRender,
                getCellProps: defaultCellProps,
            });
            i++;
        }

        i = 1;
        const endWeekMonth = daysInMonth - 7;

        while (i <= daysInMonth) {
            const date = dayjs(dateUse).set('date', i).startOf('day');
            const dayOfWeek = date.day();
            const cell: any = {
                firstWeek: rows.length < 7,
                roundStart: dayOfWeek === 0 && dayEnd && rows.length < 14,
                endOfWeek: dayOfWeek === 6,
                lastWeek: i > endWeekMonth,
                secondToLast: dayOfWeek === 6 ? 7 : dayOfWeek - 1 > dayOfWeekMonthEnd - 1,
                ignoreDay: endWeekMonth === i,
                startOfWeek: i === 1 || dayOfWeek === 0,
                startOfMonth: dayEnd,
                lastDayOfMonth: i === daysInMonth,
                dayOfWeek: dayOfWeek,
                dayOfMonth: i,
                date,
                isToday: dayjs().isSame(date, 'day'),
            };

            cell.getCellProps = makePropGetter(getHooks().getCellProps, {
                instance: getInstance(),
                cell,
            });
            cell.render = makeRender(getInstance(), getInstance().renderCell, {
                cell,
            });

            rows.push(cell);
            i++;
        }
        i = dayOfWeekMonthEnd - 1 === -1 ? 6 : dayOfWeekMonthEnd - 1;
        while (i < 7) {
            rows.push({
                value: null,
                render: defaultEmptyRender,
                getCellProps: defaultCellProps,
            });
            i++;
        }

        return rows;
    }, []);

    loopHooks(getHooks().useInstance, getInstance());

    const rows = useMemo(() => {
        const { currentDate } = state;
        return createRows(currentDate);
    }, [state]);

    const nextMonthRows = useMemo(() => {
        const { nextMonth } = state;
        return createRows(nextMonth);
    }, [state]);

    Object.assign(getInstance(), {
        rows,
        nextMonthRows,
        headers,
    });

    Object.assign(getInstance(), {
        ...state,
    });

    return getInstance();
}

type CalendarOpts = {
    _features: any[];
    initialState: { [key: string]: any };
    setState: (state: any, actionName: string) => void;
    getState: () => any;
    _getDefaultRows: () => string;
    reset: () => void;
    getRows: () => any[];
    getHeaders: () => string[];
    getNextMonth: () => void;
    getPreviousMonth: () => void;
    state: { [key: string]: any };
    selectDate: (date: dayjs.Dayjs) => void;
    onStateChange: (state: any, actionName: string) => void;
    onMonthChange: (date: dayjs.Dayjs) => void;
    options: {
        state: { [key: string]: any };
        onStateChange: (state: any, actionName: string) => void;
    };
    notfiyOnStateChanges: Set<string>;
};

type Core = CalendarOpts & {
    setOptions: (options: any) => void;
};

export function createCalendar(options: Partial<CalendarOpts>) {
    let calendar = { _features: [], options: {} } as unknown as CalendarOpts;
    const coreInitial = {
        currentDate: dayjs(),
        selectedDate: dayjs(),
    };

    let initialState = {
        ...coreInitial,
        ...options.initialState,
    };

    const queued: (() => void)[] = [];
    let queuedTimeout = false;

    const core = {
        _features: [],
        notfiyOnStateChanges: new Set<string>(options.notfiyOnStateChanges ?? []),
        initialState,
        options: {
            ...calendar.options,
            ...options.options,
        },
        _queue: (cb: () => void) => {
            queued.push(cb);
            if (!queuedTimeout) {
                Promise.resolve()
                    .then(() => {
                        while (queued.length) {
                            queued.shift()!();
                        }
                    })
                    .catch((error) => {
                        setTimeout(() => {
                            throw error;
                        });
                    });
            }
        },
        setOptions: (updater: (...args: any) => any) => {
            calendar.options = updater({ ...calendar.options });
        },
        reset: () => {
            calendar.setState(calendar.initialState, 'initial');
        },
        getState: () => {
            return calendar.options.state;
        },
        setState: (updater: any, actionName: string) => {
            calendar.options.onStateChange?.(updater, actionName);
        },
        getHeaders: memo(
            () => [DaysOfWeekSundayEnd],
            (headers) => {
                return headers;
            },
            {
                key: 'cal.headers',
            },
        ),
        _getDefaultRows: () => calendar.options.state?.currentDate || dayjs()?.format(),
        getRows: memo(
            () => [calendar.options.state?.currentDate],
            (dateValue) => {
                const rows = createCalenderMonth(calendar, dateValue);

                return rows;
            },
            {
                key: 'cal.rows',
            },
        ),
        getNextMonth: () => {
            const { currentDate } = calendar.getState() ?? calendar.initialState;
            calendar.setState((s) => {
                return {
                    ...s,
                    currentDate: dayjs(currentDate)
                        .startOf('month')
                        .add(1, 'month')
                        .startOf('month'),
                };
            }, 'currentDate');
        },
        selectDate: (date: dayjs.Dayjs) => {
            calendar.setState((s) => {
                return {
                    ...s,
                    selectedDate: date,
                };
            }, 'selectedDate');
        },
        getPreviousMonth: () => {
            const { currentDate } = calendar.getState() ?? calendar.initialState;
            calendar.setState((s) => {
                return {
                    ...s,
                    currentDate: dayjs(currentDate)
                        .startOf('month')
                        .subtract(1, 'month')
                        .startOf('month'),
                };
            }, 'currentDate');
        },
    };

    Object.assign(calendar, core);

    return calendar as Core;
}

export function useSimpleCalendar(options: Partial<CalendarOpts>) {
    const [calendarRef] = useState(() => ({
        current: createCalendar(options),
    }));

    const [state, setState] = useState(() => calendarRef.current.initialState);

    calendarRef.current.setOptions((prev) => ({
        ...prev,
        ...options,
        state: {
            ...state,
            ...options.state,
        },
        notfiyOnStateChanges: new Set<string>(options.notfiyOnStateChanges ?? []),
        onStateChange: (updater: any, actionName: string) => {
            setState(updater);
            if (calendarRef.current.notfiyOnStateChanges.has(actionName)) {
                options.onStateChange?.(updater, actionName);
            }
        },
    }));

    return calendarRef.current;
}

export type NoInfer<T> = [T][T extends any ? 0 : never];

export function memo<TDeps extends readonly any[], TResult>(
    getDeps: () => [...TDeps],
    fn: (...args: NoInfer<[...TDeps]>) => TResult,
    opts: {
        key: any;
        debug?: () => any;
        onChange?: (result: TResult) => void;
    },
): () => TResult {
    let deps: any[] = [];
    let result: TResult | undefined;

    return () => {
        let depTime: number;
        if (opts.key && opts.debug) depTime = Date.now();

        const newDeps = getDeps();

        const depsChanged =
            newDeps.length !== deps.length ||
            newDeps.some((dep: any, index: number) => deps[index] !== dep);

        if (!depsChanged) {
            return result!;
        }

        deps = newDeps;

        let resultTime: number;
        if (opts.key && opts.debug) resultTime = Date.now();

        result = fn(...newDeps);
        opts?.onChange?.(result);

        if (opts.key && opts.debug) {
            if (opts?.debug()) {
                const depEndTime = Math.round((Date.now() - depTime!) * 100) / 100;
                const resultEndTime = Math.round((Date.now() - resultTime!) * 100) / 100;
                const resultFpsPercentage = resultEndTime / 16;

                const pad = (str: number | string, num: number) => {
                    str = String(str);
                    while (str.length < num) {
                        str = ' ' + str;
                    }
                    return str;
                };

                console.info(
                    `%c⏱ ${pad(resultEndTime, 5)} /${pad(depEndTime, 5)} ms`,
                    `
            font-size: .6rem;
            font-weight: bold;
            color: hsl(${Math.max(
                0,
                Math.min(120 - 120 * resultFpsPercentage, 120),
            )}deg 100% 31%);`,
                    opts?.key,
                );
            }
        }

        return result!;
    };
}

function createCalenderMonth(calender: CalendarOpts, dateUse: string) {
    const startOfMonth = dayjs(dateUse).startOf('month');
    const endOfMonth = dayjs(dateUse).endOf('month');
    const dayOfWeekMonthStart = dayjs(startOfMonth).day();
    const dayOfWeekMonthEnd = dayjs(endOfMonth).day();
    const daysInMonth = dayjs(dateUse).daysInMonth();
    let i = 0;
    let rows = [];
    const dayEnd = dayOfWeekMonthStart;

    while (i < dayEnd) {
        rows.push({
            value: null,
            render: defaultEmptyRender,
            getCellProps: defaultCellProps,
        });
        i++;
    }

    i = 1;
    const endWeekMonth = daysInMonth - 7;

    while (i <= daysInMonth) {
        const cell = createCell(dateUse, rows, i, {
            startOfMonth,
            dayEnd,
            endWeekMonth,
            dayOfWeekMonthEnd,
            dayOfWeekMonthStart,
            daysInMonth,
            endOfMonth,
        });

        rows.push(cell);
        i++;
    }
    i = dayOfWeekMonthEnd;
    while (i < 7) {
        rows.push({
            value: null,
            render: defaultEmptyRender,
            getCellProps: defaultCellProps,
        });
        i++;
    }

    Object.assign(calender.options, { rows });

    return rows;
}

function createCell(
    dateUse: string,
    rows: any[],
    i: number,
    {
        startOfMonth,
        dayEnd,
        endWeekMonth,
        dayOfWeekMonthEnd,
        dayOfWeekMonthStart,
        daysInMonth,
    }: {
        startOfMonth: dayjs.Dayjs;
        endOfMonth: dayjs.Dayjs;
        dayOfWeekMonthStart: number;
        dayOfWeekMonthEnd: number;
        daysInMonth: number;
        dayEnd: number;
        endWeekMonth: number;
    },
) {
    const date = dayjs(dateUse).set('date', i).startOf('day');
    const dayOfWeek = date.day();
    const getRenderValue = () => cell.getValue() ?? '';
    const rowsLength = rows.length;
    const cell: any = {
        getValue: () => ({
            firstWeek: rowsLength < 7,
            roundStart: dayOfWeek === 0 && dayEnd && rowsLength < 14,
            endOfWeek: dayOfWeek === 6,
            lastWeek: i > endWeekMonth,
            secondToLast: dayOfWeek === 6 ? 7 : dayOfWeek - 1 > dayOfWeekMonthEnd - 1,
            ignoreDay: endWeekMonth === i,
            startOfWeek: i === 1 || dayOfWeek === 0,
            startOfMonth: dayEnd,
            lastDayOfMonth: i === daysInMonth,
            dayOfWeek: dayOfWeek,
            dayOfMonth: i,
            date,
            isToday: dayjs().isSame(date, 'day'),
        }),
        renderValue: getRenderValue,
        getContext: () => ({
            cell,
            getValue: cell.getValue,
            renderValue: cell.renderValue,
        }),
    };

    return cell;
}
